import React, { useState } from "react";
import { ArrowLeft, Plus, Minus, ShoppingBag } from "lucide-react";
import { Button } from "./ui/button";
import { Cart } from "./Cart";
import { toast } from "sonner";

export const TicketSelection = ({
  event,
  onBack,
  onAddToCart,
  cartItems,
  onRemoveFromCart,
  onCheckout,
}) => {
  const [selectedTickets, setSelectedTickets] = useState({});

  const isFreeEvent = !event.price || event.price === 0;

  // Use ticket_types if available (supports mixed free+paid), otherwise fallback
  const availableTickets = (event.ticket_types && event.ticket_types.length > 0)
    ? event.ticket_types.map(tt => ({
        type: tt.name,
        description: tt.description || "",
        priceMultiplier: 1,
        fixedPrice: tt.price,
        available: tt.available !== undefined ? tt.available : tt.quantity,
        quantity: tt.quantity
      }))
    : isFreeEvent
      ? [{ type: "Entrée gratuite", description: "Accès à l'événement", priceMultiplier: 0, fixedPrice: 0, available: event.available_tickets || 100, quantity: event.available_tickets || 100 }]
      : [{ type: "Standard", description: "Accès à l'événement", priceMultiplier: 1, fixedPrice: event.price, available: event.available_tickets || 0, quantity: event.available_tickets || 0 }];

  const getTicketPrice = (ticket) => {
    if (ticket.fixedPrice !== undefined) return ticket.fixedPrice;
    return event.price * ticket.priceMultiplier;
  };

  const maxPerOrder = event.max_per_order || 10;

  const handleQuantityChange = (ticketType, delta) => {
    const ticketInfo = availableTickets.find((t) => t.type === ticketType);
    const maxQty = ticketInfo ? Math.min(maxPerOrder, ticketInfo.available) : maxPerOrder;

    setSelectedTickets((prev) => {
      const currentQty = prev[ticketType] || 0;
      // Total across all types must not exceed maxPerOrder
      const totalOther = Object.entries(prev).reduce((sum, [k, v]) => k === ticketType ? sum : sum + v, 0);
      const maxForThis = Math.min(maxQty, maxPerOrder - totalOther);
      const newQty = Math.max(0, Math.min(maxForThis, currentQty + delta));
      
      if (newQty === 0) {
        const { [ticketType]: _, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [ticketType]: newQty };
    });
  };

  const getTotalPrice = () => {
    return Object.entries(selectedTickets).reduce((total, [type, qty]) => {
      const ticketInfo = availableTickets.find((t) => t.type === type);
      if (!ticketInfo) return total;
      return total + getTicketPrice(ticketInfo) * qty;
    }, 0);
  };

  const getTotalQuantity = () => {
    return Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);
  };

  const handleAddToCart = () => {
    if (getTotalQuantity() === 0) {
      toast.error("Veuillez sélectionner au moins un billet");
      return;
    }

    const cartItemsToAdd = Object.entries(selectedTickets).map(([type, quantity]) => {
      const ticketInfo = availableTickets.find((t) => t.type === type);
      return {
        event_id: event.id,
        event_title: event.title,
        event_artist: event.artist,
        event_date: event.date,
        event_venue: event.venue,
        event_image: event.image,
        event_category: event.category,
        ticket_type: type,
        quantity,
        price: ticketInfo ? getTicketPrice(ticketInfo) : 0,
        event_service_fee: event.service_fee !== undefined ? event.service_fee : 0.99,
      };
    });

    onAddToCart(cartItemsToAdd);
    toast.success(`${getTotalQuantity()} billet(s) sélectionné(s)`);
  };

  return (
    <div className="min-h-screen bg-background/40">
      {/* Header */}
      <header className="border-b border-primary/10 sticky top-0 bg-background/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              data-testid="back-btn"
              variant="ghost"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Button>
            <Cart
              items={cartItems}
              onRemoveItem={onRemoveFromCart}
              onCheckout={onCheckout}
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Event Summary */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl text-street tracking-wide mb-2">
            Sélectionnez vos billets
          </h1>
          <p className="text-xl text-muted-foreground">{event.title}</p>
          <p className="text-sm text-muted-foreground">{event.date} • {event.venue}</p>
        </div>

        {/* Ticket Options */}
        <div className="space-y-4 mb-8">
          {maxPerOrder < 10 && (
            <p className="text-xs text-muted-foreground text-center">
              Limité à {maxPerOrder} billet{maxPerOrder > 1 ? "s" : ""} par commande
            </p>
          )}
          {availableTickets.map((ticket) => {
            const price = getTicketPrice(ticket);
            const quantity = selectedTickets[ticket.type] || 0;
            const totalSelected = Object.values(selectedTickets).reduce((a, b) => a + b, 0);
            const maxForType = Math.min(maxPerOrder, ticket.available);
            const maxQty = Math.min(maxForType, maxPerOrder - totalSelected + quantity);
            const soldOut = ticket.available <= 0;

            return (
              <div
                key={ticket.type}
                data-testid={`ticket-option-${ticket.type}`}
                className={`glass-card rounded-lg p-6 ticket-option ${soldOut ? "opacity-50" : ""}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold">{ticket.type}</h3>
                      {soldOut && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs font-semibold">COMPLET</span>
                      )}
                    </div>
                    {ticket.description && <p className="text-sm text-muted-foreground mb-1">{ticket.description}</p>}
                    <p className="text-2xl font-bold text-primary">
                      {price === 0 ? "Gratuit" : `${price.toFixed(2)}€`}
                    </p>
                    {!soldOut && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Disponible
                      </p>
                    )}
                  </div>

                  {!soldOut && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-3 bg-muted/30 rounded-lg p-2">
                        <Button
                          data-testid={`decrease-${ticket.type}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => handleQuantityChange(ticket.type, -1)}
                          disabled={quantity === 0}
                          className="h-8 w-8"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center font-medium" data-testid={`quantity-${ticket.type}`}>
                          {quantity}
                        </span>
                        <Button
                          data-testid={`increase-${ticket.type}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => handleQuantityChange(ticket.type, 1)}
                          disabled={quantity >= maxQty}
                          className="h-8 w-8"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary & Add to Cart */}
        {getTotalQuantity() > 0 && (
          <div className="glass-card rounded-lg p-6 sticky bottom-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">
                  {getTotalQuantity()} billet{getTotalQuantity() > 1 ? "s" : ""} sélectionné{getTotalQuantity() > 1 ? "s" : ""}
                </div>
                <div className="text-3xl font-bold text-primary">
                  {getTotalPrice() === 0 ? "Gratuit" : `${getTotalPrice().toFixed(2)}€`}
                </div>
              </div>
              <Button
                data-testid="add-to-cart-btn"
                onClick={handleAddToCart}
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg"
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                {isFreeEvent ? "Réserver mon billet" : "Continuer vers le paiement"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
